
// ==============================
// LRC — Checkout Drawer (open/close)
// ==============================
(function initLrcCheckoutDrawer() {
    const getEls = () => ({
        overlay: document.querySelector('[data-lrc-checkout-overlay]'),
        panel: document.querySelector('[data-lrc-checkout-panel]')
    });

    const lockScroll = () => document.documentElement.classList.add('checkout-drawer-open');
    const unlockScroll = () => document.documentElement.classList.remove('checkout-drawer-open');

    function openCheckout(opener) {
        const { overlay, panel } = getEls();
        if (!overlay || !panel) return;
        overlay.style.opacity = '1';
        overlay.style.pointerEvents = 'auto';
        panel.style.transform = 'translateX(0)';
        panel.setAttribute('aria-hidden', 'false');
        lockScroll();
        requestAnimationFrame(() => {
            const focusable = panel.querySelector('button, a[href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
            focusable && focusable.focus({ preventScroll: true });
        });
        openCheckout.lastOpener = opener || document.activeElement;
    }

    function closeCheckout() {
        const { overlay, panel } = getEls();
        if (!overlay || !panel) return;
        overlay.style.opacity = '0';
        overlay.style.pointerEvents = 'none';
        panel.style.transform = 'translateX(110%)';

        const onEnd = (e) => {
            if (e && e.target !== panel) return;
            panel.removeEventListener('transitionend', onEnd, true);
            panel.setAttribute('aria-hidden', 'true');
            unlockScroll();
            const opener = openCheckout.lastOpener;
            if (opener && document.contains(opener)) opener.focus?.({ preventScroll: true });
        };
        panel.addEventListener('transitionend', onEnd, true);

        // (Fallback if transitionend is missed)
        setTimeout(() => {
            panel.removeEventListener('transitionend', onEnd, true);
            panel.setAttribute('aria-hidden', 'true');
            unlockScroll();
        }, 900);
    }

    // Delegated clicks (works if header re-renders)
    document.addEventListener('click', (e) => {
        const openBtn = e.target.closest('[data-lrc-checkout-open]');
        if (openBtn) { e.preventDefault(); openCheckout(openBtn); return; }
        const closeBtn = e.target.closest('[data-lrc-checkout-close]');
        if (closeBtn) { e.preventDefault(); closeCheckout(); return; }
    });

    // ESC to close
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') closeCheckout();
    });

    // Public API (optional)
    window.lrcCheckoutDrawer = {
        open: () => openCheckout(),
        close: closeCheckout,
        toggle: () => {
            const { panel } = getEls();
            if (!panel) return;
            const isClosed = getComputedStyle(panel).transform.includes('matrix(1, 0, 0, 1, 0, 0)') === false &&
                panel.style.transform.includes('100%');
            isClosed ? openCheckout() : closeCheckout();
        }
    };
})();


// ===============================================
// LRC — Checkout Drawer actions & navigation
// ===============================================
(function lrcCheckoutActions() {
    const CHECKOUT_URL = '/checkout';
    const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
    const $ = (sel, root = document) => root.querySelector(sel);

    // ---------- helpers ----------
    function safeParseJSON(str) {
        if (!str || typeof str !== 'string') return null;
        try { return JSON.parse(str); } catch { return null; }
    }

    // FIX: Tailwind's `.hidden{display:none!important}` → must toggle the class, not just style
    function setLoading(el, loading) {
        if (!el) return;
        const sp = el.querySelector('[data-spinner]');
        if (loading) {
            el.dataset.lrcLoading = '1';
            el.disabled = true;
            el.classList.add('opacity-50', 'pointer-events-none');
            if (sp) { sp.classList.remove('hidden'); sp.style.display = ''; }
        } else {
            delete el.dataset.lrcLoading;
            el.disabled = false;
            el.classList.remove('opacity-50', 'pointer-events-none');
            if (sp) { sp.classList.add('hidden'); sp.style.display = 'none'; }
        }
    }

    // Track last click so goToCheckout() can show spinner even without an argument
    let __lrcLastClickTarget = null;
    document.addEventListener('click', (e) => { __lrcLastClickTarget = e.target; }, { capture: true });

    function ensureSpinnerNode(btn) {
        if (!btn) return;
        let sp = btn.querySelector('[data-spinner]');
        if (!sp) {
            sp = document.createElement('span');
            sp.setAttribute('data-spinner', '');
            sp.className = 'ml-2 hidden';
            sp.innerHTML = `
        <svg class="animate-spin h-5 w-5 text-cyan-700" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>`;
            btn.appendChild(sp);
        }
    }

    function findBestCheckoutTrigger(explicitBtn) {
        if (explicitBtn) return explicitBtn;

        const fromLastClick = __lrcLastClickTarget &&
            __lrcLastClickTarget.closest('.lrc-drawer-go-to-checkout, a.lrc-drawer-go-to-checkout, button.lrc-drawer-add-to-cart-checkout, a.lrc-drawer-add-to-cart-checkout');
        if (fromLastClick) return fromLastClick;

        const active = document.activeElement;
        if (active) {
            const fromActive = active.closest('.lrc-drawer-go-to-checkout, a.lrc-drawer-go-to-checkout, button.lrc-drawer-add-to-cart-checkout, a.lrc-drawer-add-to-cart-checkout');
            if (fromActive) return fromActive;
        }

        return document.querySelector('.lrc-drawer-go-to-checkout, a.lrc-drawer-go-to-checkout');
    }

    let __lrcNavigating = false;

    // ---------- Unified goToCheckout (works from anywhere) ----------
    async function goToCheckout(triggerBtn) {
        if (__lrcNavigating) return;
        __lrcNavigating = true;

        const trigger = findBestCheckoutTrigger(triggerBtn);
        if (trigger) ensureSpinnerNode(trigger);
        if (trigger) setLoading(trigger, true);

        let finalCartData = null;
        try {
            const ps = window.lrcPlanSync;
            if (ps && typeof ps.getStatus === 'function' && typeof ps.waitForCompleteStrict === 'function') {
                const status = ps.getStatus() || {};
                if (status.state === 'running') {
                    await ps.waitForCompleteStrict();
                }
            }

            if (typeof window.lrcFlushPendingOps === 'function') {
                // await window.lrcFlushPendingOps(); // waits until flush completes
                finalCartData = await window.lrcFlushPendingOps(); // waits until flush completes
            }
        } catch (e) {
            console.warn('[LRC] flush failed before checkout:', e);
            // continue anyway
        } finally {
            const cart = finalCartData?.finalCart
            const cart_sub_discount = cart?.items.reduce((sum, li) => sum + (li?.selling_plan_allocation && Number(li?.selling_plan_allocation?.compare_at_price - li?.selling_plan_allocation?.per_delivery_price) || 0), 0);
            //console.log('cart_sub_discount: ', cart_sub_discount);
            // --- START: Klaviyo "Started Checkout"
            try {
                document.dispatchEvent(new CustomEvent('lrc:checkout-started'));
                window.LRC_klaviyo?.track?.("Checkout Started", {
                    cart_items: cart?.items || [],
                    checkout_total_cart_value: cart.total_price / 100,
                    total_price: cart.total_price / 100,
                    total_compare_at_price: (cart?.total_price + cart_sub_discount + cart?.total_discount) / 100,
                    cart_item_count: cart.item_count,
                    currency: cart.currency || "{{ shop.currency }}",
                    shopify_total_discount: cart.total_discount || 0,
                    subscription_total_discount: cart_sub_discount / 100,
                });
            } catch (e) { /* no-op */ }
            // --- END: Klaviyo "Started Checkout"
            try { window.location.assign(CHECKOUT_URL); }
            catch { window.location.href = CHECKOUT_URL; }
        }
    }

    // Expose if other scripts need it
    window.lrcGoToCheckout = goToCheckout;

    // ---------- Add-to-cart then Checkout ----------
    async function addToCartAndCheckout(btn) {
        const variantId = Number(btn?.dataset?.variantId || btn?.getAttribute('data-variant-id'));
        const qty = Math.max(1, Number(btn?.dataset?.qty || 1));
        const sellingPlanId = btn?.dataset?.sellingPlanId ? Number(btn.dataset.sellingPlanId) : null;
        const props = safeParseJSON(btn?.dataset?.properties);

        if (!Number.isFinite(variantId)) {
            console.warn('[LRC] Missing data-variant-id on .lrc-drawer-add-to-cart-checkout');
            goToCheckout(); // fallback
            return;
        }

        const line = { id: variantId, quantity: qty };
        if (sellingPlanId) line.selling_plan = sellingPlanId;
        if (props && typeof props === 'object') line.properties = props;

        setLoading(btn, true);

        try {
            const ps = window.lrcPlanSync;
            if (ps && typeof ps.getStatus === 'function' && typeof ps.waitForCompleteStrict === 'function') {
                const status = ps.getStatus() || {};
                if (status.state === 'running') {
                    await ps.waitForCompleteStrict();
                }
            }

            if (typeof window.lrcFlushPendingOps === 'function') {
                await window.lrcFlushPendingOps();
            }
        } catch (e) {
            console.warn('[LRC] flush failed before add+checkout:', e);
        }

        try {
            const res = await fetch('/cart/add.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                body: JSON.stringify({ items: [line] }),
                credentials: 'same-origin',
            });

            if (!res.ok) {
                let msg = 'Could not add to cart. Please try again.';
                try {
                    const data = await res.json();
                    if (data && (data.description || data.message)) msg = data.description || data.message;
                } catch { }
                alert(msg);
                setLoading(btn, false);
                return;
            }

            if (typeof window.lrcRefreshHeaderCartCount === 'function') {
                try { await window.lrcRefreshHeaderCartCount(); } catch { }
            }

            // We don't pass btn; unified goToCheckout will still find a trigger and show spinner
            // goToCheckout();
            const cart = await fetch('/cart.js', {
                headers: { 'Accept': 'application/json' }
            }).then(r => r.json());
            const cart_sub_discount = cart?.items.reduce((sum, li) => sum + (li?.selling_plan_allocation && Number(li?.selling_plan_allocation?.compare_at_price - li?.selling_plan_allocation?.per_delivery_price) || 0), 0);

            // --- START: Klaviyo "Started Checkout"
            try {
                document.dispatchEvent(new CustomEvent('lrc:checkout-started'));
                window.LRC_klaviyo?.track?.("Checkout Started", {
                    cart_items: cart?.items || [],
                    checkout_total_cart_value: cart.total_price / 100,
                    total_price: cart.total_price / 100,
                    total_compare_at_price: (cart?.total_price + cart_sub_discount + cart?.total_discount) / 100,
                    cart_item_count: cart.item_count,
                    currency: cart.currency || "{{ shop.currency }}",
                    shopify_total_discount: cart.total_discount || 0,
                    subscription_total_discount: cart_sub_discount / 100,
                });
            } catch (e) { /* no-op */ }

            try { window.location.assign(CHECKOUT_URL); }
            catch { window.location.href = CHECKOUT_URL; }
        } catch (err) {
            console.error('[LRC] addToCart error', err);
            alert('Network error adding to cart. Please try again.');
            setLoading(btn, false);
        }
    }

    // ---------- Delegated listeners ----------
    document.addEventListener('click', (e) => {
        const addBtn = e.target.closest('button.lrc-drawer-add-to-cart-checkout, a.lrc-drawer-add-to-cart-checkout');
        if (addBtn) {
            e.preventDefault();
            if (addBtn.dataset.lrcLoading === '1') return;
            addToCartAndCheckout(addBtn);
            return;
        }

        const goBtn = e.target.closest('.lrc-drawer-go-to-checkout, a.lrc-drawer-go-to-checkout');
        if (goBtn) {
            e.preventDefault();
            if (goBtn.dataset.lrcLoading === '1') return; // prevent double taps
            goToCheckout(goBtn);
        }
    });
})();




